import { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "../lib/api";
import PeriodBadge from "./PeriodBadge";

function monthLabel(ym) {
  if (!ym) return "";
  try {
    const [y, m] = ym.split("-").map(Number);
    const monthName = new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short" });
    return `${monthName} '${String(y).slice(-2)}`;
  } catch { return ym; }
}

const PALETTE = [
  "#0a0a0a",     // black
  "#c72c2c",     // red
  "#046a5a",     // emerald
  "#8a5a00",     // amber
  "#4b3aa0",     // indigo
  "#00668a",     // teal
];

export default function CohortComparison() {
  const [data, setData] = useState({ cohorts: [] });
  const [available, setAvailable] = useState([]); // full list of cohort months
  const [selected, setSelected] = useState([]);    // subset shown
  const [loading, setLoading] = useState(false);

  // Initial: fetch 4 most recent so the chart isn't empty
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/products/cohort-curves")
      .then((r) => {
        if (cancel) return;
        setData(r.data || { cohorts: [] });
        const months = (r.data?.cohorts || []).map((c) => c.cohort_month);
        setSelected(months);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    // fetch full list of available cohorts by hitting the tee-cohorts endpoint
    api.get("/products/printed-tee-cohorts")
      .then((r) => {
        if (cancel) return;
        setAvailable((r.data?.cohorts || []).map((c) => c.cohort_month));
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  async function refetch(months) {
    setLoading(true);
    try {
      const params = months?.length ? { cohort_months: months.join(",") } : {};
      const r = await api.get("/products/cohort-curves", { params });
      setData(r.data || { cohorts: [] });
    } finally {
      setLoading(false);
    }
  }

  function toggleCohort(cm) {
    setSelected((cur) => {
      const next = cur.includes(cm) ? cur.filter((c) => c !== cm) : [...cur, cm].slice(-6);
      refetch(next);
      return next;
    });
  }

  // Merge cohort points into a single [{week_offset, [cohort]: avg}] shape
  const merged = useMemo(() => {
    const cohorts = data?.cohorts || [];
    if (!cohorts.length) return [];
    const maxW = Math.max(...cohorts.flatMap((c) => c.points.map((p) => p.week_offset)));
    const out = [];
    for (let w = 0; w <= maxW; w++) {
      const row = { week_offset: w };
      for (const c of cohorts) {
        const p = c.points.find((pp) => pp.week_offset === w);
        if (p && p.designs_active > 0) {
          row[c.cohort_month] = p.avg_units_per_design;
        }
      }
      out.push(row);
    }
    return out;
  }, [data]);

  const cohorts = data?.cohorts || [];

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="cohort-comparison">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow">Table 14 · Age curve overlay</div>
        <div className="bm-eyebrow text-neutral-500">Weekly avg units per design, aligned to drop week</div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">Cohort comparison.</h3>
      <PeriodBadge label="All history, aligned to each drop week" note="not filtered by the date picker" className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-4">
        Overlay drop months to see which cohort peaked hardest and which had the longest tail
      </p>

      {/* Cohort picker */}
      {available.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="cohort-picker">
          <div className="bm-eyebrow text-neutral-500">Cohorts:</div>
          {available.slice(0, 18).map((cm) => {
            const on = selected.includes(cm);
            return (
              <button
                key={cm}
                onClick={() => toggleCohort(cm)}
                data-testid={`cohort-pick-${cm}`}
                className={`inline-flex items-center gap-1 px-2 py-1 bm-eyebrow border border-[#0a0a0a] ${
                  on ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"
                }`}
              >
                {monthLabel(cm)}
              </button>
            );
          })}
          {selected.length >= 6 && (
            <span className="bm-eyebrow text-neutral-500">max 6</span>
          )}
        </div>
      )}

      {loading && merged.length === 0 && <div className="text-sm text-neutral-500">Loading…</div>}

      {merged.length > 0 && (
        <div className="h-[380px]">
          <ResponsiveContainer>
            <LineChart data={merged} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#0a0a0a" opacity={0.15} />
              <XAxis
                dataKey="week_offset"
                tick={{ fontSize: 11, fill: "#0a0a0a" }}
                label={{ value: "Weeks since first sale", position: "insideBottom", offset: -5, fontSize: 11 }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#0a0a0a" }}
                label={{ value: "Avg units / design", angle: -90, position: "insideLeft", fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ background: "#f5f2eb", border: "1px solid #0a0a0a", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
                labelFormatter={(w) => `Week ${w}`}
                formatter={(v, name) => [v, monthLabel(name)]}
              />
              <Legend
                formatter={(v) => monthLabel(v)}
                wrapperStyle={{ fontSize: 11 }}
              />
              {cohorts.map((c, i) => (
                <Line
                  key={c.cohort_month}
                  type="monotone"
                  dataKey={c.cohort_month}
                  stroke={PALETTE[i % PALETTE.length]}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {cohorts.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          {cohorts.map((c, i) => (
            <div key={c.cohort_month} className="border border-[#0a0a0a]/30 bg-[#f5f2eb] p-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 inline-block" style={{ background: PALETTE[i % PALETTE.length] }} />
                <div className="font-medium">{monthLabel(c.cohort_month)}</div>
              </div>
              <div className="bm-eyebrow text-neutral-500 font-mono-bm mt-1">
                {c.design_count} designs
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
